import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	ElementRef,
	input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AsyncSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import spacetime from 'spacetime';
import { ProgressBarComponent } from 'src/lib/templates/global/progress-bar/progress-bar.component';
import { GroupValidationModule } from 'src/lib/templates/layout/group-validation/group-validation.module';
import { ModalMoverComponent } from '../../../../global/modal-mover/modal-mover.component';
import { PaginationTableColumnDirective } from '../../children/pagination-table-column.directive';
import { IPaginationTable } from '../../core/pagination-table.interface';
import { PaginationTableSavedVisibleColumnConfiguration } from '../../types/ipagination-table-user-preferences';
import { PaginationTableExportConfig } from './pagination-table-exporter/pagination-table-export-config';
import { PaginationTableExporterComponent } from './pagination-table-exporter/pagination-table-exporter.component';

interface PaginationTableExportModalForm {
	columns: FormArray<FormControl<boolean>>;
	allItems: FormControl<boolean>;
	startIndex: FormControl<number>;
	endIndex: FormControl<number>;
}

@Component({
	selector: 'ae-pagination-table-export-modal',
	templateUrl: './pagination-table-export-modal.component.html',
	styleUrls: ['./pagination-table-export-modal.component.scss'],
	imports: [
		FormsModule,
		GroupValidationModule,
		ModalMoverComponent,
		NgTemplateOutlet,
		PaginationTableExporterComponent,
		ProgressBarComponent,
		ReactiveFormsModule,
	],
})
export class PaginationTableExportModalComponent<T>
	implements OnDestroy, OnInit
{
	private _unsubscribe$ = new AsyncSubject<null>();

	readonly pgTable = input.required<IPaginationTable<T>>();
	readonly columnTemplates =
		input.required<PaginationTableColumnDirective<T>[]>();
	readonly visibleColumns =
		input.required<PaginationTableSavedVisibleColumnConfiguration>();

	@ViewChild('downloadLink') downloadLink: ElementRef<HTMLElement>;

	public exportableColumns: {
		id: string;
		column: PaginationTableColumnDirective<T>;
	}[] = [];
	public configForm: FormGroup<PaginationTableExportModalForm>;

	public exportConfig: PaginationTableExportConfig<T>;
	public exportProgressValue: { complete: number; total: number } = {
		complete: 0,
		total: 0,
	};
	public downloadReady: boolean = false;

	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
	) {}

	ngOnInit(): void {
		const columnTemplates = this.columnTemplates();
		const visibleColumns = this.visibleColumns();
		const pgTable = this.pgTable();

		const columnsGroup = this.fb.array<boolean>(
			[],
			[
				(group: FormArray<FormControl<boolean>>) => {
					const val = group.value;
					if (!val.find((x) => x)) {
						return { noColumns: true };
					}
					return null;
				},
			],
		);
		this.exportableColumns = columnTemplates
			.filter((c) => c.isExportable() && !c.disabled())
			.map((c) => {
				return {
					id: c.columnId(),
					column: c,
				};
			});

		this.exportableColumns.forEach((c, i) => {
			const ctrl = this.fb.control<boolean>(
				visibleColumns[c.id] != null && visibleColumns[c.id],
			);
			columnsGroup.insert(i, ctrl);
		});

		this.configForm = this.fb.group<PaginationTableExportModalForm>({
			columns: columnsGroup,
			allItems: new FormControl(true),
			startIndex: new FormControl({ value: 1, disabled: true }, [
				Validators.required,
				Validators.min(1),
			]),
			endIndex: new FormControl({ value: pgTable.totalCount, disabled: true }, [
				Validators.required,
				Validators.min(1),
			]),
		});

		this.configForm.controls.allItems.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((v) => {
				if (v) {
					this.configForm.controls.startIndex.disable();
					this.configForm.controls.endIndex.disable();
				} else {
					this.configForm.controls.startIndex.enable();
					this.configForm.controls.endIndex.enable();
				}
			});
	}

	public select = (action: 'all' | 'none') => {
		this.configForm.controls.columns.controls.forEach((c) =>
			c.setValue(action === 'all'),
		);
	};

	public exportProgress = (progress: { complete: number; total: number }) => {
		this.exportProgressValue = progress;
	};

	public exportComplete = (csvContent: string) => {
		this.exportProgressValue.complete = this.exportProgressValue.total;

		this.downloadLink.nativeElement.className = 'd-block';
		const blob = new Blob([csvContent], { type: 'text/csv' });

		this.downloadLink.nativeElement.setAttribute(
			'href',
			URL.createObjectURL(blob),
		);

		this.downloadLink.nativeElement.setAttribute(
			'download',
			`${this.pgTable().name()} ${spacetime
				.now()
				.format('{iso-month}-{date-pad}-{year} {hour}_{minute}{ampm}')}.csv`,
		);
		this.downloadLink.nativeElement.click();

		this.downloadReady = true;
	};

	public start = () => {
		if (this.exportConfig == null) {
			this.exportConfig = {
				pgTable: this.pgTable(),
				exportColumns: this.configForm.controls.columns.controls
					.map((c, i) => (c.value ? this.exportableColumns[i].id : null))
					.filter((x) => x != null),
				exportRange: this.configForm.controls.allItems.value
					? [-1, -1]
					: [
							this.configForm.controls.startIndex.value,
							this.configForm.controls.endIndex.value,
						],
			};
		}
	};

	public cancel = () => {
		this.activeModal.dismiss();
	};

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
